import ContestPageContestViewHeader
  from "components/contests/contest-details/header/contest-page-contest-view-header.tsx";
import React, { useState } from "react";
import styles from "components/contests/contest-details/styles.module.css";
import { ContestItem, } from "components/contests/contest-display-items";
import { Contest, ContestEntry } from "interfaces/leaderboard/contest.ts";
import { sparketBlack, sparketGold, } from "utils/constants.ts";
import ViewLeaderboardButton from "./view-leaderboard-button.tsx";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import BlockyBoldText from "components/custom-texts/blocky-bold-text.tsx";
import { showSignIn } from "state/slices/login-modals-slice.ts";
import { useAuth } from "hooks/auth.tsx";
import { getGroupState, requestToJoinGroup } from "state/slices/group-slice.ts";
import { UserGroupMembershipStatus } from "utils/userGroupMembershipStatus.ts";
import JoinGroupModal from "home/join-group-button/join-group-modal";
import { checkIfUserCanEnterContest } from "utils/contestEntryUtils";
import EnterContestButton from "components/contests/contest-display-items/enter-contest-button.tsx";
import { getCalculatedContestEntryState, getCurrentEntryIdByContestId } from "state/slices/contests-slice.ts";

interface Props {
  contest: Contest;
  userContestEntries: ContestEntry[];
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const ContestPageContestView = ({
                                  contest,
                                  userContestEntries,
                                  setShowModal,
                                }: Props) => {
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const group = useAppSelector(getGroupState);

  const currentContestEntryId = useAppSelector(state => getCurrentEntryIdByContestId(state, contest.id));
  const calculatedContestEntryState = useAppSelector(state => getCalculatedContestEntryState(state, currentContestEntryId));

  const [showJoinGroupModal, setShowJoinGroupModal] = useState(false);

  const canEnterContest = checkIfUserCanEnterContest(contest, userContestEntries);

  const showLeaderboardButton = currentContestEntryId !== undefined;

  const onEnterContestButtonClick = () => {
    if (!auth.signedIn) {
      dispatch(showSignIn());
      return;
    }

    if (group.name && !(group.status === UserGroupMembershipStatus.APPROVED)) {
      setShowJoinGroupModal(true);
      return;
    }

    setShowModal(true);
  };

  const resolveContestEntryStatusInfoMessage = () => {
    if (!calculatedContestEntryState || calculatedContestEntryState.remainingNumberOfPicksOnDistinctContestantsForEntryToBeValid === 3) {
      return `Make at least 3 picks with different contestants to enter!`;
    }

    if (calculatedContestEntryState.remainingNumberOfPicksOnDistinctContestantsForEntryToBeValid === 2) {
      return `Make at least 2 picks with different contestants to enter!`;
    }

    return `Make at least 1 more pick with a different contestant to enter!`;
  };

  return (
    <>
      <div style={{ backgroundColor: sparketBlack }}>
        <ContestPageContestViewHeader contestId={contest.id} userContestEntries={userContestEntries} />

        <div className={styles.wrapper}>
          <ContestItem
            contest={contest}
            userContestEntries={userContestEntries}
            showStatus
          />

          {showLeaderboardButton && canEnterContest ? (
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "10px"
            }}>
              <div style={{ flex: 1 }}>
                <ViewLeaderboardButton contestId={contest.id} />
              </div>
              <div style={{ flex: 1 }}>
                <EnterContestButton onClick={onEnterContestButtonClick} text="Enter Again" />
              </div>
            </div>
          ) : showLeaderboardButton ? (
            <ViewLeaderboardButton contestId={contest.id} />
          ) : canEnterContest ? (
            <EnterContestButton onClick={onEnterContestButtonClick} />
          ) : null}

          <div>
            {calculatedContestEntryState && !calculatedContestEntryState.isValid && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  color: sparketGold,
                  fontSize: 12,
                  marginBottom: 10,
                }}
              >
                <BlockyBoldText>
                  {resolveContestEntryStatusInfoMessage()}
                </BlockyBoldText>
              </div>
            )}
          </div>
        </div>
      </div>
      <JoinGroupModal
        acceptAndJoin={() => {
          dispatch(requestToJoinGroup(group.id));
          setShowJoinGroupModal(false);
          if (!currentContestEntryId) {
            setShowModal(true);
          }
        }}
        show={showJoinGroupModal}
        close={() => setShowJoinGroupModal(false)}
      />
    </>
  );
};

export default ContestPageContestView;
